import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Global Textarea — auto-grow + unlimited length by default.
 *
 * Spec: every Description / Notes / Reason / Comment textarea in the CRM
 * must:
 *   - Accept unlimited text (no maxLength)
 *   - Default to ~6 visible lines (rows={6})
 *   - Auto-expand as the user types (height = scrollHeight)
 *
 * `maxLength` is intentionally NOT forwarded — pass-through props could
 * accidentally re-introduce a cap. If a legitimate hard cap is needed
 * (e.g. a tweet composer), use a plain native <textarea> instead.
 */
const Textarea = React.forwardRef(({ className, value, defaultValue, rows = 6, onInput, maxLength: _ignored, ...props }, ref) => {
  const internalRef = React.useRef(null);
  const setRef = (node) => {
    internalRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };

  const fit = React.useCallback(() => {
    const el = internalRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  React.useEffect(() => { fit(); }, [value, defaultValue, fit]);

  React.useEffect(() => {
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [fit]);

  return (
    <textarea
      ref={setRef}
      value={value}
      defaultValue={defaultValue}
      rows={rows}
      onInput={(e) => { fit(); if (onInput) onInput(e); }}
      className={cn(
        "flex min-h-[140px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-none overflow-hidden",
        className
      )}
      {...props} />
  );
})
Textarea.displayName = "Textarea"

export { Textarea }
